import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Calendar, Image as ImageIcon,
  ClipboardList, Camera, LogOut, User, Receipt
} from "lucide-react";

const adminLinks = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/bookings", label: "Bookings", icon: Calendar },
  { to: "/admin/portfolio", label: "Portfolio", icon: ImageIcon },
  { to: "/admin/projects", label: "Projects", icon: Camera },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/tasks", label: "Tasks", icon: ClipboardList },
];

const clientLinks = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/dashboard/projects", label: "Projects", icon: Camera },
  { to: "/dashboard/bookings", label: "Bookings", icon: Calendar },
  { to: "/dashboard/payments", label: "Payments", icon: Receipt },
  { to: "/dashboard/profile", label: "Profile", icon: User },
];

export default function DashboardLayout({ children, role = "admin" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const links = role === "admin" ? adminLinks : clientLinks;

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden md:flex w-64 flex-col bg-[hsl(var(--sidebar-background))] text-[hsl(var(--sidebar-foreground))]">
        <div className="h-20 flex items-center px-6 border-b border-[hsl(var(--sidebar-border))]">
          <Link to="/" className="font-serif text-2xl tracking-tight">Dobhai<span className="text-[#C9D1B5]">Official</span></Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/admin" || to === "/dashboard"}
              data-testid={`side-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm tracking-wide transition-colors ${
                  isActive
                    ? "bg-[hsl(var(--sidebar-accent))] text-white"
                    : "text-[hsl(var(--sidebar-foreground))]/80 hover:bg-[hsl(var(--sidebar-accent))] hover:text-white"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.25} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[hsl(var(--sidebar-border))] text-xs">
          <div className="mb-2 opacity-80">{user?.name}</div>
          <div className="mb-3 opacity-60">{user?.email}</div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            className="flex items-center gap-2 text-sm hover:text-white"
            data-testid="side-logout"
          >
            <LogOut size={14} strokeWidth={1.25} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between h-16 px-4 border-b border-border bg-background">
          <Link to="/" className="font-serif text-xl tracking-tight">DobhaiOfficial</Link>
          <button onClick={async () => { await logout(); nav("/"); }} className="text-sm">Sign out</button>
        </header>
        <main className="flex-1 p-6 md:p-10 overflow-auto">{children}</main>
      </div>
    </div>
  );
}